/**
 * Display formatting. **The only place numbers become strings.**
 *
 * **The rule that governs this file:** the backend sends every exchange
 * number as a JSON *string*, because `Decimal` has no JSON representation
 * and a float round-trip is a real loss (see `app/db/models.py`). So the
 * API types in `src/api/types.ts` declare those fields as `DecimalString`,
 * and the only place they become a JavaScript `number` is here — at the
 * moment of drawing them on a screen.
 *
 * Nothing in this app does arithmetic on money. If a total is needed, the
 * backend computes it. That keeps `0.1 + 0.2` out of the one place the
 * owner reads his account balance from.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  ONE RENDERER PER CONCEPT
 * ─────────────────────────────────────────────────────────────────────
 *
 *   money / balances / P&L   `formatMoney(value, asset)`   `1,240.50 USDT`
 *   prices                   `formatPrice(value)`          `68,142.50`
 *   percentages              `formatPercent(value)`        `-1.20%`
 *   R-multiples              `formatR(value)`              `+2.35R`
 *   quantities (base asset)  `formatQuantity(value)`       `0.00014`
 *   candle/analysis times    `formatUtc(value)`            `2026-03-14 08:30 UTC`
 *   operational times        `formatLocal(value)`          browser local time + zone
 *
 * If a component is building a number string with template literals or
 * `toFixed`, it is doing this file's job and will drift from it.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  WHY THERE IS NO CURRENCY SYMBOL ANYWHERE
 * ─────────────────────────────────────────────────────────────────────
 *
 * This platform trades crypto pairs. The quote asset is whatever the pair
 * says it is — `BTCUSDT` settles in USDT, not in dollars and certainly not
 * in euros. USDT is *not* USD: it is a token with its own issuer, its own
 * redemption risk and its own de-peg history, and rendering 1,240.50 USDT
 * as "$1,240.50" tells the owner he holds something he does not hold.
 *
 * So money is rendered as `1,240.50 USDT` — magnitude first, asset code
 * after — with no `€`, no `$` and no locale currency formatting. The
 * trailing code also keeps decimal points aligned down a table column,
 * which a variable-width leading symbol does not.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  AND WHY TWO DECIMALS IS NOT A DEFAULT EITHER
 * ─────────────────────────────────────────────────────────────────────
 *
 * The euro sign was half the bug. The other half was `toFixed(2)`, which
 * is only correct because the quote asset happens to be a stablecoin. On an
 * `ETHBTC` pair the P&L is denominated in BTC, and two decimals renders a
 * real 0.00014 BTC profit as `0.00 BTC` — a number that says the trade did
 * nothing. Precision is a property of the asset, not of money in general,
 * so {@link moneyDecimalsFor} derives it from the asset code and falls back
 * to magnitude (never to 2) for an asset it does not recognise. Rounding an
 * unknown asset to cents is the one direction it must not fail in.
 *
 * Where the venue's own precision is known — `SymbolDetailOut.price_precision`
 * and `quantity_precision` from `GET /api/market/symbols/{symbol}` — pass it
 * as `decimals` and it beats the magnitude guess. That is the exact number of
 * places the exchange will accept, and it is the one this app should print —
 * up to the ceiling below, which beats everything.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  FOUR PLACES IS THE CEILING ON MONEY, AND IT IS NOT NEGOTIABLE  [D16]
 * ─────────────────────────────────────────────────────────────────────
 *
 * The owner's instruction is flat: **never more than four decimal places on
 * a money, quantity or percentage figure.** {@link MAX_DISPLAY_DECIMALS} is
 * that rule, and it is applied here rather than at forty call sites — a
 * ceiling enforced by convention is a ceiling that lasts until the next
 * component. For those figures it outranks every other source of precision,
 * a `decimals` the caller passed included. There is no opt-out for them,
 * deliberately: an escape hatch is how "platform-wide" becomes "everywhere
 * except the two screens that asked nicely".
 *
 * **What that costs, stated out loud so nobody re-derives it.** The section
 * above exists because `toFixed(2)` renders a real 0.00014 BTC profit as
 * `0.00 BTC` — a number that says the trade did nothing. Four places is a
 * far higher floor than two (0.00014 BTC still reads as `0.0001`), but the
 * same failure is reachable below 0.00005 of any asset. So rounding is
 * capped, and *silent* rounding to zero is not permitted: a non-zero value
 * that would print as all zeros renders as `<0.0001` instead. Fewer digits
 * is the instruction; claiming a position does not exist is not, and the two
 * are separable. See {@link underflowLabel}.
 *
 * **A price is the exception, because a price is a fact about the venue, not
 * a rounded readout of money.** [M5] is flat the other way: one price at the
 * venue's own tick precision on every surface that shows it. A 0.00001-tick
 * pair (TRXUSDT, XLMUSDT, HBARUSDT — `price_precision` 5) shown to four
 * places is the list disagreeing with the header about the exchange, the
 * exact defect M5 names; and a price shown to *more* places than the tick
 * invites a limit the venue rejects with -1013 PRICE_FILTER. So prices carry
 * their own ceiling, {@link MAX_PRICE_DECIMALS}, and render at the venue tick
 * up to it — pass the venue's `price_precision` as `decimals` to
 * {@link formatPrice}. This is the same exemption the chart's own price axis
 * already had: klinecharts draws it from the `pricePrecision` prop rather
 * than through this module, because clamping a sub-cent pair (SHIBUSDT quotes
 * at 0.000008) to `0.0000` is the underflow failure above wearing an axis
 * label. The readout and the axis now agree on why they are exempt — and the
 * money ceiling above is untouched by any of it.
 */

import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';

import { pnlTextClass } from './colors.ts';

/** A `Decimal` as it arrives over the wire: an exact base-10 string. */
export type DecimalString = string;

export { sumDecimalStrings } from './decimal.ts';

/**
 * Parse a wire number for display only.
 *
 * Returns `NaN` for anything unparseable rather than `0` — a zero balance
 * and an unknown balance must not look the same on screen.
 */
export function toNumber(value: DecimalString | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return Number.NaN;
  return typeof value === 'number' ? value : Number(value);
}

/**
 * What every formatter here renders for an absent value.
 *
 * Re-exported at the foot of this file. The Database tab draws NULLs directly
 * rather than through a formatter — in a database browser a null IS the
 * information — so it uses this glyph rather than inventing a second
 * convention in one component.
 */
const PLACEHOLDER = '—';

export interface NumberFormatOptions {
  /** Fixed decimal places. Omit to pick a sensible number from magnitude. */
  decimals?: number;
  /** Prefix with an explicit + when positive. Used for PnL and R. */
  signed?: boolean;
  /** Thousands separators. Off for tick-level prices where they add noise. */
  grouped?: boolean;
  /**
   * The ceiling on decimal places for this call. Defaults to
   * {@link MAX_DISPLAY_DECIMALS}, the money/quantity rule [D16]. Only
   * {@link formatPrice} raises it — to {@link MAX_PRICE_DECIMALS}, so a price
   * renders at its venue tick [M5]. It is not an escape hatch for money: see
   * the "FOUR PLACES IS THE CEILING" section of this file's header.
   */
  maxDecimals?: number;
}

/**
 * The platform-wide ceiling on displayed decimal places. [D16]
 *
 * Every renderer in this file clamps to it, and there is no way past it —
 * not a `decimals` option, not the venue's `price_precision`. See the
 * "FOUR PLACES IS THE CEILING" section of this file's header for what that
 * buys and what it costs.
 */
export const MAX_DISPLAY_DECIMALS = 4;

/**
 * The ceiling on displayed decimal places for a **price** — the one figure the
 * money ceiling above does not govern. [M5]
 *
 * A price renders at the venue's own tick precision (`price_precision`), and
 * this is only the backstop against a pathological value: eight is a satoshi,
 * the deepest precision any USDT pair on this venue quotes, and it matches the
 * `BTC` entry in {@link MONEY_DECIMALS} and the chart price axis. The top-20
 * futures universe tops out at five (TRXUSDT/XLMUSDT/HBARUSDT), well inside it;
 * the backstop exists so an unexpected `price_precision` can never spill the
 * whole layout. See this file's header for why prices are exempt from the
 * four-place money ceiling and money is not.
 */
export const MAX_PRICE_DECIMALS = 8;

/**
 * Clamp any precision — derived or caller-supplied — to a ceiling.
 *
 * `ceiling` is {@link MAX_DISPLAY_DECIMALS} for every money, quantity and
 * percentage figure and only {@link MAX_PRICE_DECIMALS} for a price; nothing
 * else raises it.
 */
function capDecimals(decimals: number, ceiling: number = MAX_DISPLAY_DECIMALS): number {
  if (!Number.isFinite(decimals)) return ceiling;
  return Math.min(ceiling, Math.max(0, Math.trunc(decimals)));
}

/**
 * `<0.0001` rather than `0.0000`, for a value too small to render honestly.
 *
 * Returns `null` when the number rounds to something visible, which is the
 * overwhelmingly common case and the only branch worth optimising for.
 *
 * The ceiling is an instruction about *digits*. "This position is worth
 * nothing" is a claim about *money*, and rounding is not allowed to make it
 * on the platform's behalf — a dust balance printed as `0.0000` is the same
 * believable lie as an indicator the engine never computed. The sign is
 * preserved because a small loss and a small profit are not the same fact.
 */
function underflowLabel(parsed: number, decimals: number): string | null {
  if (parsed === 0) return null;
  const smallest = 10 ** -decimals;
  // `toLocaleString` rounds half away from zero, so anything at or above
  // half of the last place still prints a non-zero digit.
  if (Math.abs(parsed) >= smallest / 2) return null;
  const bound = smallest.toFixed(decimals);
  return parsed > 0 ? `<${bound}` : `>-${bound}`;
}

/**
 * Choose decimal places from magnitude, within the ceiling.
 *
 * A price of 68,142.5 needs one place; a price of 0.00003412 would like
 * eight and gets four, because four is the rule. The ladder below still
 * matters for everything between: it is what keeps a large balance from
 * wearing four pointless zeros while a small one keeps every place it is
 * allowed.
 *
 * This function used to extend to 20 places for sub-satoshi dust. That is
 * now {@link underflowLabel}'s job — the scale stops at four and says
 * `<0.0001` rather than growing to fit.
 */
export function autoDecimals(value: number): number {
  const magnitude = Math.abs(value);
  if (!Number.isFinite(magnitude)) return 2;
  if (magnitude === 0) return 2;
  if (magnitude >= 100) return 2;
  return MAX_DISPLAY_DECIMALS;
}

export function formatNumber(
  value: DecimalString | number | null | undefined,
  options: NumberFormatOptions = {},
): string {
  const parsed = toNumber(value);
  if (!Number.isFinite(parsed)) return PLACEHOLDER;

  const decimals = capDecimals(
    options.decimals ?? autoDecimals(parsed),
    options.maxDecimals ?? MAX_DISPLAY_DECIMALS,
  );
  const underflow = underflowLabel(parsed, decimals);
  if (underflow !== null) return underflow;
  const body = parsed.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: options.grouped ?? true,
  });
  // Only a strictly positive number gets a `+`. Zero is flat, and "+0.00"
  // reads as a win — see `toneForValue` in lib/colors, which colours it
  // neutral for the same reason.
  if (options.signed && parsed > 0) return `+${body}`;
  return body;
}

// ── Quote asset ───────────────────────────────────────────────────────

/**
 * The quote asset to assume when the pair is not known.
 *
 * `BTCUSDT` is the default instrument (see `store/uiStore`), so USDT is the
 * asset every balance, limit and P&L figure is denominated in until a
 * component knows better. Anywhere the pair *is* known, pass its real
 * `quote_asset` — `SymbolDetail.quote_asset` from the API, or
 * `quoteAssetFor(symbol)` below.
 *
 * Import this instead of writing `'EUR'`, `'USD'` or `'USDT'` inline. The
 * whole point of a single constant is that changing venue or quote asset is
 * one edit rather than forty.
 *
 * `components/settings/venue.ts` used to declare a second constant of the
 * same name — two sources of truth for the account's denomination, which is
 * the exact defect both were written to remove. It now re-exports this one.
 * The layering decides which survives: `lib/` is imported by `components/`,
 * never the reverse.
 */
export const QUOTE_ASSET = 'USDT';

/**
 * Quote assets, longest first so `USDT` is matched before `USD` and
 * `ETHBTC` splits into ETH/BTC rather than E/THBTC.
 *
 * Mirrors `app/risk/limits.py::_QUOTE_ASSETS`. Kept as a string parse on
 * purpose: it has to work for a symbol whose exchange metadata has not been
 * fetched yet, which is exactly when a label is about to be rendered.
 */
const QUOTE_ASSETS: readonly string[] = [
  'FDUSD',
  'TUSD',
  'USDC',
  'USDT',
  'BUSD',
  'USDD',
  'DAI',
  'USD',
  'EUR',
  'GBP',
  'TRY',
  'BRL',
  'JPY',
  'AUD',
  'BTC',
  'XBT',
  'ETH',
  'BNB',
];

/**
 * The quote asset of a pair: `BTCUSDT` -> `USDT`, `ETHBTC` -> `BTC`.
 *
 * Returns `QUOTE_ASSET` for anything unrecognised rather than an empty
 * string — a bare number with no asset beside it is the ambiguity this
 * whole section exists to remove.
 */
export function quoteAssetFor(symbol: string | null | undefined): string {
  if (!symbol) return QUOTE_ASSET;
  const upper = symbol.toUpperCase().replace(/[/\-_:]/g, '');
  for (const quote of QUOTE_ASSETS) {
    if (upper.length > quote.length && upper.endsWith(quote)) return quote;
  }
  return QUOTE_ASSET;
}

/** The base asset of a pair: `BTCUSDT` -> `BTC`. The thing you own. */
export function baseAssetFor(symbol: string | null | undefined): string {
  if (!symbol) return '';
  const upper = symbol.toUpperCase().replace(/[/\-_:]/g, '');
  for (const quote of QUOTE_ASSETS) {
    if (upper.length > quote.length && upper.endsWith(quote)) {
      return upper.slice(0, upper.length - quote.length);
    }
  }
  return upper;
}

// ── Money ─────────────────────────────────────────────────────────────

/**
 * Quote assets this app can price exactly, and to how many places.
 *
 * Stablecoins and fiat settle in cents. A crypto-quoted pair (`ETHBTC`,
 * `SOLETH`) does not: eight places is a satoshi, and anything less rounds a
 * real result to zero.
 */
const MONEY_DECIMALS: Readonly<Record<string, number>> = {
  USDT: 2,
  USDC: 2,
  BUSD: 2,
  FDUSD: 2,
  TUSD: 2,
  USDD: 2,
  DAI: 2,
  USD: 2,
  EUR: 2,
  GBP: 2,
  TRY: 2,
  BRL: 2,
  AUD: 2,
  JPY: 0,
  BTC: 8,
  XBT: 8,
  ETH: 6,
  BNB: 4,
};

/**
 * Decimal places an amount of `asset` is worth showing.
 *
 * A stablecoin balance means cents; a BTC balance would mean satoshis, and
 * gets four places like everything else — {@link MAX_DISPLAY_DECIMALS} is
 * the ceiling and it applies to this table too. The entries above 4 are
 * kept as written rather than edited down, so the table still records what
 * each asset's *natural* precision is if the ceiling is ever lifted.
 *
 * An asset that is not in the table falls back to magnitude rather than to
 * 2 — because the failure mode of guessing 2 is printing `0.00` for a
 * position that exists.
 *
 * The venue's own `price_precision` beats this whenever the caller has it;
 * pass it as `decimals` to {@link formatMoney}. The ceiling beats both.
 */
export function moneyDecimalsFor(
  asset: string | null | undefined,
  value?: DecimalString | number | null,
): number {
  const code = (asset ?? '').trim().toUpperCase();
  const known = MONEY_DECIMALS[code];
  if (known !== undefined) return capDecimals(known);
  return autoDecimals(toNumber(value ?? Number.NaN));
}

/**
 * **The** money renderer: `1,240.50 USDT`, `-8.00 USDT`, `0.00014000 BTC`.
 *
 * Magnitude first, asset code after. Precision comes from the asset (see
 * {@link moneyDecimalsFor}), never from an assumption that money means
 * cents. Pass `decimals` to override with the venue's own precision.
 *
 * No currency symbol, ever — see this file's header. `€` and `$` are lies
 * about what is in the account.
 */
export function formatMoney(
  value: DecimalString | number | null | undefined,
  asset: string = QUOTE_ASSET,
  options: NumberFormatOptions = {},
): string {
  const code = (asset ?? '').trim().toUpperCase();
  const body = formatNumber(value, {
    ...options,
    decimals: options.decimals ?? moneyDecimalsFor(code, value),
  });
  if (body === PLACEHOLDER) return PLACEHOLDER;
  return code.length > 0 ? `${body} ${code}` : body;
}

/**
 * Money in the quote asset of a *pair*: `formatMoneyOf('ETHBTC', x)`.
 *
 * For per-row figures in a table that spans instruments. A book holding
 * BTCUSDT and ETHBTC has two denominations in one P&L column, and printing
 * both as USDT is the same class of lie as printing either as euros.
 */
export function formatMoneyOf(
  symbol: string | null | undefined,
  value: DecimalString | number | null | undefined,
  options: NumberFormatOptions = {},
): string {
  return formatMoney(value, quoteAssetFor(symbol), options);
}

/**
 * @deprecated Use {@link formatMoney}. Kept as an exact alias so the two
 * names cannot render the same amount two different ways — which is what
 * they used to do, one with a leading `€` and one with a trailing code.
 */
export const formatCurrency = formatMoney;

/**
 * The sentence shown before anything opens a position:
 * `risking 8.00 USDT (1.0%)`.
 *
 * Amount first, percentage second, and never percentage alone. Under
 * pressure a percentage is an abstraction and an amount is not — "1%" is
 * easy to click past in a way that "8.00 USDT" is not. This is the
 * platform's core thesis rendered as a string: the amount you are about to
 * lose is stated before the trade, not discovered after it.
 */
export function formatRiskStatement(
  riskAmount: DecimalString | number | null | undefined,
  riskPercent: DecimalString | number | null | undefined,
  asset: string = QUOTE_ASSET,
): string {
  const amount = formatMoney(riskAmount, asset);
  const percent = formatPercent(riskPercent, { decimals: 1 });
  if (amount === PLACEHOLDER && percent === PLACEHOLDER) return 'risk unknown — do not proceed';
  // "of account value", not "of equity": equity is correct trading English
  // but it collides with the equities market in a crypto-only product, and
  // this string is read at the moment a position is about to open.
  if (amount === PLACEHOLDER) return `risking ${percent} of account value`;
  if (percent === PLACEHOLDER) return `risking ${amount}`;
  return `risking ${amount} (${percent})`;
}

/**
 * A price, at the venue's tick precision. [M5]
 *
 * Pass the venue's `price_precision` as `decimals` and it renders to exactly
 * that many places — the same number the header and the chart axis are driven
 * by, so one instrument reads the same on every surface. Unlike money, a price
 * is allowed past the four-place ceiling up to {@link MAX_PRICE_DECIMALS},
 * because the tick is a fact about the exchange (TRXUSDT quotes to five): see
 * this file's header. With no `decimals` the scale comes from magnitude,
 * still under the price ceiling.
 */
export function formatPrice(
  value: DecimalString | number | null | undefined,
  decimals?: number,
): string {
  return formatNumber(value, {
    maxDecimals: MAX_PRICE_DECIMALS,
    ...(decimals === undefined ? {} : { decimals }),
  });
}

/**
 * A percentage that already arrived as a percentage: `12.5` -> `12.50%`.
 *
 * Every `*_pct` field in `api/types.ts` is in percent units, 0–100. If a
 * value is a *fraction* (0–1) use {@link formatFractionAsPercent} — mixing
 * the two silently renders a 45% drawdown as 0.45%.
 */
export function formatPercent(
  value: DecimalString | number | null | undefined,
  options: NumberFormatOptions = {},
): string {
  const body = formatNumber(value, { decimals: 2, ...options });
  return body === PLACEHOLDER ? PLACEHOLDER : `${body}%`;
}

/** A fraction rendered as a percentage: `0.125` -> `12.50%`. */
export function formatFractionAsPercent(
  value: DecimalString | number | null | undefined,
  options: NumberFormatOptions = {},
): string {
  const parsed = toNumber(value);
  if (!Number.isFinite(parsed)) return PLACEHOLDER;
  return formatPercent(parsed * 100, options);
}

/**
 * Basis points: `7.5` -> `7.5 bps`, and `bpsAsPercent(7.5)` -> `0.075%`.
 *
 * Fees and slippage are configured in bps and reported in percent, which is
 * two units for one quantity and a 100× error waiting to happen. Both
 * spellings live here so no component invents its own conversion.
 */
export function formatBps(value: DecimalString | number | null | undefined): string {
  const body = formatNumber(value, { decimals: 1 });
  return body === PLACEHOLDER ? PLACEHOLDER : `${body} bps`;
}

/** Basis points shown as a percentage: `7.5` bps -> `0.075%`. */
export function bpsAsPercent(
  value: DecimalString | number | null | undefined,
  options: NumberFormatOptions = {},
): string {
  const parsed = toNumber(value);
  if (!Number.isFinite(parsed)) return PLACEHOLDER;
  return formatPercent(parsed / 100, { decimals: 3, ...options });
}

/**
 * R-multiples, always to two places: `-1.00R`, `+2.35R`, `0.00R`.
 *
 * R is the number the owner should be reading first, so it gets a fixed,
 * instantly comparable shape. Zero is unsigned — a scratch trade is not a
 * winner, and `+0.00R` claims otherwise.
 *
 * Accepts a `DecimalString` because that is how R arrives over the wire.
 */
export function formatR(value: DecimalString | number | null | undefined): string {
  const parsed = toNumber(value);
  if (!Number.isFinite(parsed)) return PLACEHOLDER;
  return `${parsed > 0 ? '+' : ''}${parsed.toFixed(2)}R`;
}

/**
 * A quantity of the **base** asset: `0.00014`, `1,250,000`, `12.5`.
 *
 * Crypto is fractional and a position is routinely a five-figure fraction
 * of a coin, so this must never round to two places and show `0.00`. It
 * must also never fall back to exponential notation — `1e-7` is not a
 * number a human reads under pressure — which is what
 * `Number.prototype.toString` does below 1e-6.
 *
 * Capped at {@link MAX_DISPLAY_DECIMALS} like everything else, which makes
 * the underflow guard load-bearing here rather than theoretical: dropping
 * trailing zeros would render a 0.00000012 BTC position as a bare `0`, and
 * `<0.0001` is what it says instead.
 *
 * Trailing zeros are dropped (`0.1000` -> `0.1`) and thousands are grouped,
 * because a quantity of DOGE is as likely as a quantity of BTC.
 */
export function formatQuantity(
  value: DecimalString | number | null | undefined,
  options: { decimals?: number; asset?: string } = {},
): string {
  const parsed = toNumber(value);
  if (!Number.isFinite(parsed)) return PLACEHOLDER;

  const maximumFractionDigits = capDecimals(options.decimals ?? autoDecimals(parsed));
  const underflow = underflowLabel(parsed, maximumFractionDigits);
  if (underflow !== null) {
    const code = options.asset?.trim().toUpperCase();
    return code ? `${underflow} ${code}` : underflow;
  }
  const body = parsed.toLocaleString('en-US', {
    // No minimum: a whole number of coins should not wear four zeros.
    minimumFractionDigits: 0,
    maximumFractionDigits,
    useGrouping: true,
  });
  const asset = options.asset?.trim().toUpperCase();
  return asset ? `${body} ${asset}` : body;
}

/**
 * A quantity of the base asset of a *pair*: `0.00014 BTC` for `BTCUSDT`.
 *
 * `precision` is the venue's `quantity_precision` when the caller has the
 * symbol detail loaded — the exact number of places the exchange will accept
 * for this instrument. Without it the scale comes from magnitude, which is
 * wide enough to never show a real position as `0`. Two decimals, the thing
 * this used to inherit from the money formatter, is wrong for every crypto
 * quantity there is.
 */
export function formatQuantityOf(
  symbol: string | null | undefined,
  value: DecimalString | number | null | undefined,
  precision?: number | null,
): string {
  const base = baseAssetFor(symbol);
  return formatQuantity(value, {
    ...(precision === null || precision === undefined ? {} : { decimals: precision }),
    ...(base ? { asset: base } : {}),
  });
}

/** Large counts: 1.2K, 45.3M. Only for volume — never for money. */
export function formatCompact(value: DecimalString | number | null | undefined): string {
  const parsed = toNumber(value);
  if (!Number.isFinite(parsed)) return PLACEHOLDER;
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(parsed);
}

// ── Time ──────────────────────────────────────────────────────────────
//
// Stored instants remain UTC. Candle identities, backtest windows and market
// watermarks render in labelled UTC so they match Binance exactly. Operational
// events render in the viewer's labelled local timezone so alerts, runner
// activity and recovery evidence line up with the clock the operator is using.

export function toDate(value: string | number | Date): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  return parseISO(value);
}

function utcShift(date: Date): Date {
  return new Date(date.getTime() + date.getTimezoneOffset() * 60_000);
}

/** `2026-03-14 08:30 UTC` */
export function formatUtc(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return PLACEHOLDER;
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return PLACEHOLDER;
  return `${format(utcShift(date), 'yyyy-MM-dd HH:mm')} UTC`;
}

/**
 * The viewer's short zone name at `date` (`GMT+2`, `EDT`, `UTC`), or `local`
 * when the platform reports none. One helper, so every local time on screen
 * carries the same label.
 */
export function localZoneLabel(date: Date): string {
  return (
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value ?? 'local'
  );
}

/**
 * `2026-03-14 22:30 GMT+2` — in the VIEWER'S local timezone, labelled with its
 * short zone so a LIVE readout lines up with the wall clock and is never
 * mistaken for UTC. Use for operator events (alerts, orders, fills, recovery,
 * runner observations). Candle and analytical timestamps stay `formatUtc`.
 */
export function formatLocal(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return PLACEHOLDER;
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return PLACEHOLDER;
  // date-fns `format` renders in the system's local zone — no `utcShift` here.
  return `${format(date, 'yyyy-MM-dd HH:mm')} ${localZoneLabel(date)}`;
}

/**
 * `14:03:27 GMT+2` — local wall-clock time to the second, zone-labelled. For
 * "not refreshed since …" copy, where the date is today and the seconds matter.
 */
export function formatLocalTime(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return PLACEHOLDER;
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return PLACEHOLDER;
  return `${format(date, 'HH:mm:ss')} ${localZoneLabel(date)}`;
}

/** `2026-03-14` */
export function formatUtcDate(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return PLACEHOLDER;
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return PLACEHOLDER;
  return format(utcShift(date), 'yyyy-MM-dd');
}

/** `08:30:15` — for a live feed where the date is obvious. */
export function formatUtcTime(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return PLACEHOLDER;
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return PLACEHOLDER;
  return format(utcShift(date), 'HH:mm:ss');
}

export function formatRelative(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return PLACEHOLDER;
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return PLACEHOLDER;
  return `${formatDistanceToNowStrict(date)} ago`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return PLACEHOLDER;
  }
  const total = Math.max(0, Math.round(seconds));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${total % 60}s`;
  return `${total}s`;
}

/** ISO date (`yyyy-MM-dd`) for `<input type="date">`, in UTC. */
export function toDateInputValue(value: string | number | Date): string {
  const date = toDate(value);
  return format(utcShift(date), 'yyyy-MM-dd');
}

/** Midnight UTC of a `<input type="date">` value, as an ISO instant. */
export function fromDateInputValue(value: string, endOfDay = false): string {
  return `${value}T${endOfDay ? '23:59:59' : '00:00:00'}Z`;
}

/** `4h 12m` from a fractional-hours field such as `TradeOut.duration_hours`. */
export function formatDurationHours(hours: number | null | undefined): string {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return PLACEHOLDER;
  return formatDuration(hours * 3600);
}

/** `1.4 GB`. For the data-store views, where disk footprint is the point. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return PLACEHOLDER;
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unit]}`;
}

// ── Direction helpers ─────────────────────────────────────────────────
//
// These delegate to `lib/colors.ts`, which owns every semantic colour
// decision. Two places deciding what colour a loss is means one of them
// will eventually be wrong in one theme.

export { pnlTextClass as pnlClass, pnlBgClass, pnlBorderVar, pnlColorVar } from './colors.ts';

/** Tailwind text colour for a signed wire value. Zero is neutral, not green. */
export function pnlClassFrom(value: DecimalString | number | null | undefined): string {
  return pnlTextClass(toNumber(value));
}

/**
 * Sign glyph for a value rendered separately from its number.
 *
 * ASCII `-`, matching what `toLocaleString` puts in front of every negative
 * number this file produces. A typographic minus (U+2212) is prettier and
 * would mean the same figure is spelled two ways depending on which helper
 * drew it — and it does not survive a copy-paste into a spreadsheet.
 */
export function signGlyph(value: DecimalString | number | null | undefined): string {
  const parsed = toNumber(value);
  if (!Number.isFinite(parsed) || parsed === 0) return '';
  return parsed > 0 ? '+' : '-';
}

/**
 * Direction as a shape rather than a colour: `▲` / `▼` / `·`.
 *
 * For anywhere the up/down distinction is currently carried by red-vs-green
 * alone. Roughly 1 in 12 men have some colour-vision deficiency, and the
 * CVD palette helps but does not cover a printout or a greyscale screenshot.
 */
export function directionGlyph(value: DecimalString | number | null | undefined): string {
  const parsed = toNumber(value);
  if (!Number.isFinite(parsed)) return PLACEHOLDER;
  if (parsed > 0) return '▲';
  if (parsed < 0) return '▼';
  return '·';
}

export { PLACEHOLDER };
